/**
 * **A choice answer drawn as the options that were PICKED, one chip each.** Owner, 5 November 2026:
 * in the report's ODPOWIEDZI band „Pon, Wt, Śr, Czw", „Średniozaawansowany" and „Hantle, Sztanga,
 * Ławka" were a wall of text. Only the picked options are drawn, never the ones left out — the
 * equipment question alone offers 25.
 *
 * Which answers get chips:
 * - every multi choice — the training days, the equipment, a trainer's own list;
 * - the single choice of a catalog LADDER (`experience`, `activity_level`), as one chip, because it
 *   is a level the trainer scans for rather than a sentence they read.
 *
 * Everything else keeps its plain text: a gender, a date, free text, a goal, and a trainer's own
 * single choice, whose one word is already the clearest rendering of it.
 *
 * The training days are put in WEEK order and printed in the short weekday names („Pn", „Czw") the
 * rest of the app's week strips use, whatever order the client tapped them in. Chips need nothing
 * from the snapshot's options, so a snapshot frozen without them is drawn the same way.
 *
 * Pure: `t` arrives as a parameter.
 */

import type { TranslateQuestionKey } from '~/shared/lib/standardQuestionNames';
import { standardOptionLabel } from '~/shared/lib/standardQuestionNames';

export interface SurveyChoiceItemView {
  /** The option's own code — „Monday", „Intermediate", „dumbbell" — stable across languages. */
  readonly key: string;
  readonly label: string;
}

export interface SurveyChoiceListView {
  /** The picked options, never empty. */
  readonly items: readonly SurveyChoiceItemView[];
}

/** The catalog's training-days question. Its options are the seven `DayOfWeek` members. */
const TRAINING_DAYS_CODE = 'availability';

/** The catalog codes whose single choice is a level on a ladder. */
const LADDER_CODES: ReadonlySet<string> = new Set(['experience', 'activity_level']);

/** The `DayOfWeek` members in week order, each with its key under `common.weekdayShort`. */
const WEEKDAYS: readonly (readonly [string, string])[] = [
  ['Monday', 'mon'],
  ['Tuesday', 'tue'],
  ['Wednesday', 'wed'],
  ['Thursday', 'thu'],
  ['Friday', 'fri'],
  ['Saturday', 'sat'],
  ['Sunday', 'sun'],
];

export interface ChoiceQuestion {
  readonly code: string | null;
  readonly answerType: string | null;
}

export interface ChoiceAnswer {
  readonly text: string | null;
  readonly choices: readonly string[];
}

/**
 * The picked options of one choice answer as chips, or `null` when the answer keeps its plain text.
 *
 * @param t the translator the option names are read through.
 */
export function choiceListOf(
  t: TranslateQuestionKey,
  question: ChoiceQuestion,
  answer: ChoiceAnswer,
): SurveyChoiceListView | null {
  // A single choice arrives in `text` or as a one-member `choices`, depending on the build that sent it.
  const picked = answer.choices.length > 0 ? answer.choices : answer.text === null ? [] : [answer.text];
  if (picked.length === 0) return null;

  const ladder = question.code !== null && LADDER_CODES.has(question.code);
  const chips =
    question.answerType === 'MultiChoice' || (ladder && question.answerType === 'SingleChoice');
  if (!chips) return null;

  const named = (choice: string): SurveyChoiceItemView => ({
    key: choice,
    label: standardOptionLabel(t, question.code, choice),
  });

  if (question.code === TRAINING_DAYS_CODE) {
    const days = WEEKDAYS.filter(([day]) => picked.includes(day)).map(([day, short]) => ({
      key: day,
      label: t(`common.weekdayShort.${short}`),
    }));
    // A member this build does not know is not dropped: it follows the week, under its own name.
    const unknown = picked.filter((choice) => !WEEKDAYS.some(([day]) => day === choice));
    return { items: [...days, ...unknown.map(named)] };
  }

  return { items: picked.map(named) };
}
